import CheckMarkIcon from '../../../../icons/svg/checkmark_circle.svg?react';
import CrossCircleIcon from '../../../../icons/svg/cross_circle.svg?react';
import DeleteIcon from '../../../../icons/svg/delete_trash.svg?react';
import HelpIcon from '../../../../icons/svg/help.svg?react';
import InfoIcon from '../../../../icons/svg/info.svg?react';
import WarningIcon from '../../../../icons/svg/warning.svg?react';
import {
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
  SvgIcon,
  SvgIconProps,
  SxProps,
  Theme,
  IconButton
} from '@mui/material';
import { ElementType, FC } from 'react';
import { useTranslation } from 'react-i18next';
import { CloseIcon } from '../../../../icons';
import { Button } from '../../../atomic/button';

export { ConfirmationDialogWithWarnings } from './withWarnings';

type Variant =
  | 'neutral'
  | 'success'
  | 'question'
  | 'information'
  | 'warning'
  | 'error'
  | 'delete'
  | 'overwrite';

type Props = {
  open: boolean;
  onConfirm?: () => void;
  onClose?: () => void;
  onCancel?: () => void;
  title?: string;
  contentText?: string;
  contentComponent?: JSX.Element;
  confirmButtonText?: string;
  confirmButtonDisabled?: boolean;
  cancelButtonText?: string;
  hideSaveButton?: boolean;
  hideCancelButton?: boolean;
  width?: string;
  fullScreen?: boolean;
  variant: Variant;
  sx?: SxProps<Theme>;
};

export type ConfirmationDialogProps = Props;

type VariantSpecification = {
  icon?: ElementType;
  iconColor?: SvgIconProps['color'];
  headingColor?: SvgIconProps['color'];
  buttonColor?: 'error' | 'midnight';
};

const VARIANT_SPECIFICATIONS_MAP: { [key in Variant]: VariantSpecification } = {
  neutral: {},
  success: {
    icon: CheckMarkIcon,
    iconColor: 'success'
  },
  question: {
    icon: HelpIcon,
    iconColor: 'primary'
  },
  information: {
    icon: InfoIcon,
    iconColor: 'info'
  },
  warning: {
    icon: WarningIcon,
    iconColor: 'warning'
  },
  error: {
    icon: CrossCircleIcon,
    iconColor: 'error'
  },
  delete: {
    icon: DeleteIcon,
    iconColor: 'error',
    buttonColor: 'error'
  },
  overwrite: {
    icon: WarningIcon,
    iconColor: 'error',
    buttonColor: 'error'
  }
};

/**
 * ConfirmationDialog is used to render a simple dialog with two buttons to either confirm or cancel a previously
 * chosen action with different variants, for example 'delete'.
 */
export const ConfirmationDialog: FC<Props> = ({
  open,
  onClose,
  onConfirm,
  onCancel,
  title,
  contentText,
  contentComponent,
  confirmButtonText,
  confirmButtonDisabled,
  cancelButtonText,
  hideCancelButton,
  hideSaveButton,
  width,
  fullScreen,
  variant
}) => {
  const { t } = useTranslation('common');
  const {
    icon,
    iconColor = 'primary',
    headingColor = 'midnight',
    buttonColor = 'midnight'
  } = VARIANT_SPECIFICATIONS_MAP[variant];

  const handleConfirm = () => {
    onConfirm?.();
    onClose?.();
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
    onClose?.();
  };

  return (
    <Dialog
      PaperProps={{
        className: `text-center shadow-900 m-auto`,
        style: { width: width ?? '320px' }
      }}
      fullScreen={fullScreen}
      open={open}
      onClose={onClose}
    >
      <DialogTitle
        sx={{
          paddingTop: '24px',
          paddingBottom: '12px',
          fontSize: '40px',
          lineHeight: 'normal',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '16px'
        }}
      >
        {onClose && (
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              position: 'absolute',
              padding: 0,
              right: 24,
              top: 24,
              color: (theme) => theme.palette.grey[900]
            }}
          >
            <CloseIcon />
          </IconButton>
        )}
        {icon && <SvgIcon component={icon ?? 'div'} color={iconColor} fontSize="inherit" />}
        <Typography
          variant="h3"
          component="span"
          fontWeight={'bold'}
          color={`${headingColor}.main`}
        >
          {title}
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ paddingBottom: '24px', display: 'flex', flexDirection: 'column' }}>
        <DialogContentText
          color="primary"
          sx={{
            whiteSpace: 'pre-line',
            textAlign: 'center',
            marginBottom: '20px'
          }}
        >
          {contentText}
        </DialogContentText>
        {contentComponent}
        {!hideSaveButton && (
          <Button
            color={buttonColor}
            onClick={handleConfirm}
            disabled={confirmButtonDisabled}
            className="mb-2 mt-3"
          >
            {confirmButtonText ?? t('CONFIRM')}
          </Button>
        )}
        {!hideCancelButton && (
          <Button variant="secondary" onClick={handleCancel}>
            {cancelButtonText ?? t('CANCEL')}
          </Button>
        )}
      </DialogContent>
    </Dialog>
  );
};
